<script setup lang="ts">
import logoUrl from "@/assets/icons/logo.svg?url";
import { Clock3, Mail } from "lucide-vue-next";
import {
  getCategorySearchPath,
  type SearchCategoryOption,
} from "~/utils/search-catalog";

const { t } = useI18n();
const localeRoute = useLocaleRoute();
const { openCookiePreferences } = useCookieConsent();

const { data: categoriesData } = await useFetch<SearchCategoryOption[]>(
  "/api/categories",
  {
    key: "footer-categories",
    default: () => [],
  },
);

const popularCategories = computed(() =>
  (categoriesData.value || []).slice(0, 8),
);

function formatCategoryName(value: string) {
  const shortKey = `category_short.${value}`;
  const shortTranslated = t(shortKey);

  if (shortTranslated !== shortKey) return shortTranslated;

  const key = `category.${value}`;
  const translated = t(key);

  return translated === key ? value : translated;
}

const informationLinks = [
  { label: "footer.information.items.about", to: "/about" },
  { label: "footer.information.items.how_it_works", to: "/how-it-works" },
  {
    label: "footer.information.items.for_professionals",
    to: "/for-professionals",
  },
  { label: "footer.information.items.faq", to: "/faq" },
  {
    label: "footer.information.items.privacy_policy",
    to: "/privacy-policy",
  },
  { label: "footer.information.items.terms", to: "/terms" },
  { label: "footer.information.items.cookies", to: "/cookies" },
] as const;
</script>

<template>
  <footer class="bg-app-text text-white">
    <div class="mx-auto w-full max-w-[1280px] px-6 py-12 lg:py-14">
      <div
        class="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.15fr_1fr_1fr_1fr] lg:gap-12"
      >
        <!-- Brand -->
        <div>
          <NuxtLink
            :to="localeRoute('/')"
            class="inline-flex items-center gap-0.5"
          >
            <img
              :src="logoUrl"
              alt="Senta.lt"
              class="size-8 brightness-0 invert"
            />

            <span
              class="font-gabarito text-2xl font-bold uppercase leading-none text-white"
            >
              {{ t("footer.brand") }}
            </span>
          </NuxtLink>

          <p class="mt-4 max-w-[300px] text-sm leading-6 text-white/40">
            {{ t("footer.description") }}
          </p>
        </div>

        <!-- Popular Categories -->
        <div>
          <h3 class="font-semibold text-white">
            {{ t("footer.popular_categories.header") }}
          </h3>

          <ul class="mt-5 space-y-4">
            <li v-for="category in popularCategories" :key="category.id">
              <NuxtLink
                :to="localeRoute(getCategorySearchPath(category.name))"
                class="text-sm text-white/40 transition duration-300 hover:text-white"
              >
                {{ formatCategoryName(category.name) }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- Information -->
        <div>
          <h3 class="font-semibold text-white">
            {{ t("footer.information.header") }}
          </h3>

          <ul class="mt-5 space-y-4">
            <li v-for="link in informationLinks" :key="link.label">
              <NuxtLink
                :to="localeRoute(link.to)"
                class="text-sm text-white/40 transition duration-300 hover:text-white"
              >
                {{ t(link.label) }}
              </NuxtLink>
            </li>
            <li>
              <button
                type="button"
                class="text-left text-sm text-white/40 transition duration-300 hover:text-white"
                @click="openCookiePreferences"
              >
                {{ t("footer.information.items.cookie_settings") }}
              </button>
            </li>
          </ul>
        </div>

        <!-- Contacts -->
        <div>
          <h3 class="font-semibold text-white">
            {{ t("footer.contacts.header") }}
          </h3>

          <p class="mt-5 max-w-[230px] text-sm leading-6 text-white/40">
            {{ t("footer.contacts.description") }}
          </p>

          <div class="mt-4 space-y-3">
            <a
              :href="`mailto:${t('footer.contacts.email')}`"
              class="inline-flex items-center gap-2 text-sm text-white/50 transition duration-300 hover:text-white animated-underline"
            >
              <Mail class="size-4 shrink-0 text-primary" />
              <span>{{ t("footer.contacts.email") }}</span>
            </a>

            <div class="flex items-center gap-2 text-sm text-white/50">
              <Clock3 class="size-4 shrink-0 text-primary" />
              <span>{{ t("footer.contacts.working_hours") }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-12 border-t border-white/15 pt-5">
        <p class="text-sm text-white/30">
          {{ t("footer.copyright") }}
        </p>
      </div>
    </div>
  </footer>
</template>
